import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, BookOpenIcon, FlameIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, MapPinIcon, UserIcon, GlobeIcon, LinkIcon, ZapIcon, BookmarkIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
// Mock data for representation tags
const representationTags = [{
  id: 'lgbtq',
  name: 'LGBTQ+',
  description: 'Books featuring lesbian, gay, bisexual, transgender, and queer characters and themes.',
  count: 328,
  category: 'Sexual Orientation & Gender'
}, {
  id: 'lesbian',
  name: 'Lesbian',
  description: 'Books featuring lesbian characters or relationships.',
  count: 187,
  category: 'Sexual Orientation & Gender'
}, {
  id: 'gay',
  name: 'Gay',
  description: 'Books featuring gay characters or relationships.',
  count: 215,
  category: 'Sexual Orientation & Gender'
}, {
  id: 'bisexual',
  name: 'Bisexual',
  description: 'Books featuring bisexual characters or themes.',
  count: 156,
  category: 'Sexual Orientation & Gender'
}, {
  id: 'transgender',
  name: 'Transgender',
  description: 'Books featuring transgender characters or themes.',
  count: 134,
  category: 'Sexual Orientation & Gender'
}, {
  id: 'nonbinary',
  name: 'Non-binary',
  description: 'Books featuring non-binary or genderqueer characters.',
  count: 98,
  category: 'Sexual Orientation & Gender'
}, {
  id: 'asexual',
  name: 'Asexual',
  description: 'Books featuring asexual characters or themes.',
  count: 76,
  category: 'Sexual Orientation & Gender'
}, {
  id: 'neurodivergent',
  name: 'Neurodivergent',
  description: 'Books with characters who are on the autism spectrum, have ADHD, dyslexia, or other neurodivergent conditions.',
  count: 156,
  category: 'Neurodiversity & Disability'
}, {
  id: 'autism',
  name: 'Autism',
  description: 'Books featuring characters on the autism spectrum.',
  count: 87,
  category: 'Neurodiversity & Disability'
}, {
  id: 'adhd',
  name: 'ADHD',
  description: 'Books featuring characters with Attention Deficit Hyperactivity Disorder.',
  count: 65,
  category: 'Neurodiversity & Disability'
}, {
  id: 'physical-disability',
  name: 'Physical Disability',
  description: 'Books featuring characters with physical disabilities.',
  count: 124,
  category: 'Neurodiversity & Disability'
}, {
  id: 'mental-health',
  name: 'Mental Health',
  description: 'Books featuring characters dealing with mental health conditions.',
  count: 198,
  category: 'Mental Health'
}, {
  id: 'anxiety',
  name: 'Anxiety',
  description: 'Books featuring characters with anxiety disorders.',
  count: 145,
  category: 'Mental Health'
}, {
  id: 'depression',
  name: 'Depression',
  description: 'Books featuring characters with depression.',
  count: 132,
  category: 'Mental Health'
}, {
  id: 'ptsd',
  name: 'PTSD',
  description: 'Books featuring characters with Post-Traumatic Stress Disorder.',
  count: 118,
  category: 'Mental Health'
}, {
  id: 'body-positive',
  name: 'Body Positive',
  description: 'Books featuring body positive themes and characters of diverse body types.',
  count: 167,
  category: 'Body Diversity'
}, {
  id: 'plus-size',
  name: 'Plus Size',
  description: 'Books featuring plus size or fat characters in positive representation.',
  count: 134,
  category: 'Body Diversity'
}];
const Representation: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  // Helper function to get color based on representation category
  const getRepresentationColor = (category: string) => {
    if (darkMode) {
      switch (category) {
        case 'Sexual Orientation & Gender':
          return 'bg-pink-900/30 text-pink-300';
        case 'Race & Ethnicity':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'Neurodiversity & Disability':
          return 'bg-blue-900/30 text-blue-300';
        case 'Mental Health':
          return 'bg-green-900/30 text-green-300';
        case 'Body Diversity':
          return 'bg-indigo-900/30 text-indigo-300';
        default:
          return dk.badgeNeutral;
      }
    }
    switch (category) {
      case 'Sexual Orientation & Gender':
        return 'bg-pink-100 text-pink-900';
      case 'Race & Ethnicity':
        return 'bg-indigo-100 text-indigo-900';
      case 'Neurodiversity & Disability':
        return 'bg-blue-100 text-blue-900';
      case 'Mental Health':
        return 'bg-green-100 text-green-900';
      case 'Body Diversity':
        return 'bg-indigo-100 text-indigo-900';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };
  const groupedTags = representationTags.reduce((acc, tag) => {
    const category = tag.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tag);
    return acc;
  }, {} as Record<string, typeof representationTags>);
  const filteredGroupedTags = Object.entries(groupedTags).reduce((acc, [category, tags]) => {
    const filteredTags = tags.filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()) || tag.description.toLowerCase().includes(searchQuery.toLowerCase()));
    if (filteredTags.length > 0) acc[category] = filteredTags;
    return acc;
  }, {} as Record<string, typeof representationTags>);
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    isActive: true
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  return <div className={dk.page} data-id="element-12563" data-mp="kb1ttwgoyja7">
      <ContentPageHeader title="Representation" icon={getContentPageIcon('representation')} searchPlaceholder="Search representation tags..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-12564" data-mp="kbtmoziwm2ws" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-12565" data-mp="182pdpufjcu2z">
        <div className="flex gap-8" data-id="element-12566" data-mp="138l5l7ujag0b">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-12567" data-mp="1albzg5yxnzf4">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={[]} onReset={() => setSearchQuery('')} searchable={false} data-id="element-12568" data-mp="1kigrgmfgmp3u" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-12569" data-mp="1yj9k11m7ba4b">
            {/* Cultural Identity callout */}
            <Link to="/cultural-identity" className={`flex items-center justify-between gap-4 rounded-xl border p-4 mb-6 transition-colors ${darkMode ? 'bg-indigo-900/20 border-indigo-700/50 hover:bg-indigo-900/30' : 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'}`} data-id="element-12570" data-mp="1ygwuo3e45xh8">
              <div className="flex items-center gap-3" data-id="element-12571" data-mp="2h9hjn9qozifr">
                <GlobeIcon className={`h-6 w-6 flex-shrink-0 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-12572" data-mp="2ha0r1wipuofj" />
                <div data-id="element-12573" data-mp="d6fyuof8l2yf">
                  <p className={`font-semibold text-sm ${darkMode ? 'text-indigo-300' : 'text-indigo-800'}`} data-id="element-12574" data-mp="d6z5h8zfs4x5">
                    Looking for Cultural Identity?
                  </p>
                  <p className={`text-xs mt-0.5 ${darkMode ? 'text-indigo-400/80' : 'text-indigo-600'}`} data-id="element-12575" data-mp="m5b5g1m70f29">
                    Browse 60 identities across 10 world regions — from Oceania
                    to the Global Diaspora.
                  </p>
                </div>
              </div>
              <span className={`text-sm font-medium whitespace-nowrap ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-12576" data-mp="15uljzca7ctys">
                Explore →
              </span>
            </Link>
            {Object.keys(filteredGroupedTags).length === 0 ? <div className="text-center py-12" data-id="element-12577" data-mp="37o1924qn60rn">
                <p className={dk.textMuted} data-id="element-12578" data-mp="2xj2sa1fk1h9l">
                  No representation tags found matching your search.
                </p>
              </div> : searchQuery ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-12579" data-mp="2nsqb8j6y8687">
                {Object.values(filteredGroupedTags).flat().map(tag => <Link key={tag.id} to={`/tagged-books/${tag.id}`} className={`${getRepresentationColor(tag.category)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-12580" data-mp="2dtrb5p091iwt">
                      <div className="flex justify-between items-center mb-1" data-id="element-12581" data-mp="24vp1ym6d3jyg">
                        <div className="font-medium" data-id="element-12582" data-mp="29sp80x37ompk">{tag.name}</div>
                        <div className="text-xs opacity-80" data-id="element-12583" data-mp="330xyalw8shm">
                          {tag.count} books
                        </div>
                      </div>
                      {tag.description && <p className="text-xs mt-1 opacity-90" data-id="element-12584" data-mp="242w2q5tfbq9d">
                          {tag.description}
                        </p>}
                    </Link>)}
              </div> : <div className="space-y-3" data-id="element-12585" data-mp="1u7vs9a3pxy3e">
                {sortEntriesAlphabetically(Object.entries(filteredGroupedTags)).map(([category, tags]) => <ContentAccordionSection key={category} title={category} itemCount={tags.length} data-id="element-12586" data-mp="1l7fqq66jd116">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-12587" data-mp="1bg2uz3hm9qpv">
                      {tags.map(tag => <Link key={tag.id} to={`/tagged-books/${tag.id}`} className={`${getRepresentationColor(tag.category)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-12588" data-mp="12evabo80woe9">
                          <div className="flex justify-between items-center mb-1" data-id="element-12589" data-mp="39582rjuyv76x">
                            <div className="font-medium" data-id="element-12590" data-mp="3e0dnwggjohty">{tag.name}</div>
                            <div className="text-xs opacity-80" data-id="element-12591" data-mp="3i4v6wdf2yr3o">
                              {tag.count} books
                            </div>
                          </div>
                          {tag.description && <p className="text-xs mt-1 opacity-90" data-id="element-12592" data-mp="2qegw4q2d5uw8">
                              {tag.description}
                            </p>}
                        </Link>)}
                    </div>
                  </ContentAccordionSection>)}
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default Representation;